import React, {useState} from "react";
import axios from "axios";
import {useQuery} from "react-query";
import {format, parseISO} from "date-fns";
import styled from "styled-components";
import colours from "../../../styles/appColours";
import {useSearchParams} from "react-router-dom";
import toast from "react-hot-toast";
import {useRoles} from "../../../hooks/useRoles";

const Baseurl = process.env.REACT_APP_BASEURL;
const getToken = localStorage.getItem("eftd-admin-token");

const getConfig = () => {
  let token = null;
  try {
    if (getToken) {
      const tokenObject = JSON.parse(getToken);
      token = tokenObject?.data?.data?.token;
    }
  } catch (e) {
    console.error("Failed to parse token from localStorage", e);
  }
  if (!token) {
    return null;
  }
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

const SubscribeAction = ({subscriptionData, refetchSubscription}: any) => {
  const {scope} = useRoles();
  const user = subscriptionData?.user;
  const plan = subscriptionData?.plan;
  const isSubscriptionActive = subscriptionData.status === "ACTIVE";
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCanceling, setIsCanceling] = useState(false);
  const [add, setAdd] = useState(false);
  const [credit, setCredit] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // Get current page from URL or default to 1
  const currentPage = Number(searchParams.get("page")) || 1;
  const limit = 7;

  const userId = user?.id;
  const Baseurl = process.env.REACT_APP_BASEURL;
  const getToken: any = localStorage.getItem("eftd-admin-token");
  const tokenObject: any = JSON.parse(getToken);
  const token = tokenObject?.data?.data?.token;
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // Fetch subscription history
  const {
    data: allSubscriptions,
    isLoading: isLoadingHistory,
    isError: isErrorHistory,
    error: historyError,
  } = useQuery(
    ["SUBSCRIBER_HISTORY", userId, currentPage, limit],
    async () => {
      const config = getConfig();
      if (!config || !Baseurl || !userId) {
        throw new Error(
          "Authentication token, Base URL, or User ID not available for history fetch."
        );
      }
      const historyEndpoint = `${Baseurl}/api/admin/subscription/subscribers/history?page=${currentPage}&limit=${limit}&filter=userId:${userId}`;
      const response = await axios.get(historyEndpoint, config);
      return response.data?.data || response.data;
    },
    {
      enabled: !!userId,
      staleTime: 0,
      cacheTime: 0,
      onError: (err) => {
        console.error("Failed to load subscription history:", err);
      },
    }
  );

  const handlePageChange = (newPage: number) => {
    searchParams.set("page", newPage.toString());
    setSearchParams(searchParams);
  };

  // Generate page numbers with ellipsis
  const getPageNumbers = () => {
    if (!allSubscriptions?.meta?.total) return [];

    const totalPages = Math.ceil(allSubscriptions.meta.total / limit);
    const current = currentPage;
    const delta = 2;
    const range = [];

    for (
      let i = Math.max(2, current - delta);
      i <= Math.min(totalPages - 1, current + delta);
      i++
    ) {
      range.push(i);
    }

    if (current - delta > 2) {
      range.unshift("...");
    }
    if (current + delta < totalPages - 1) {
      range.push("...");
    }

    range.unshift(1);
    if (totalPages > 1) {
      range.push(totalPages);
    }

    return range;
  };

  const cancelSub = async () => {
    setIsCanceling(true);
    try {
      await axios.patch(
        `${Baseurl}/api/admin/subscription/${subscriptionData.id}/${userId}/cancel-sub`,
        {},
        config
      );

      toast.success("Subscription canceled!");
      refetchSubscription();
    } catch (err) {
      console.error(err);
      toast.error("Failed to cancel sub");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (credit <= 0) {
      setError("Credit must be greater than 0");
      return;
    }

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      await axios.post(
        `${Baseurl}/api/admin/subscription/${subscriptionData.id}/${userId}/add-credit`,
        {credit: String(credit)},
        config
      );

      toast.success("Credit added successfully!");
      refetchSubscription();
      setCredit(1);
      setAdd(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to add credit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      {/* User Detail Section */}
      <div className="mb-5">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <img
              src={
                user?.imageUrl || "https://www.gravatar.com/avatar/?d=mp&s=100"
              }
              alt={
                user?.businessName ||
                user?.businessContactInfo?.fullName ||
                "User"
              }
              className="w-16 h-16 rounded-full object-cover border border-gray-300"
            />
            <div>
              <h3 className="text-xl font-semibold text-gray-800">
                {user?.businessName ||
                  user?.businessContactInfo?.fullName ||
                  "N/A"}
              </h3>
              <span
                className={`px-4 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full
               ${
                 subscriptionData?.status === "ACTIVE"
                   ? "bg-green-100 text-green-800"
                   : subscriptionData?.status === "PENDING_ACTIVATION"
                   ? "bg-orange-100 text-orange-800"
                   : subscriptionData?.status === "CANCELLED"
                   ? "bg-red-100 text-red-800"
                   : "bg-gray-100 text-gray-800"
               }`}
              >
                {subscriptionData?.status || "N/A"}
              </span>
            </div>
          </div>

          <div className="flex gap-4">
            {isSubscriptionActive && scope === "write" && (
              <button
                onClick={() => {
                  cancelSub();
                }}
                className="px-6 py-2 bg-red-500 disabled:bg-red-500/30 text-white rounded-md font-medium hover:bg-red-600 transition duration-200"
                disabled={isCanceling}
              >
                {isCanceling ? "Canceling" : "Cancel Subscription"}
              </button>
            )}
            {scope === "write" && (
              <button
                className="px-6 py-2 border border-orange-500 text-orange-500 rounded-md font-medium transition duration-200"
                onClick={() => {
                  if (subscriptionData.status !== "ACTIVE") {
                    toast.error("Subscription not active!");
                    return;
                  }
                  setAdd(!add);
                }}
              >
                {add ? "Close Add Credit" : "Add Credit +"}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-4 border-b border-b-[#E2E2E2] pb-2 mt-4">
        <p className="flex items-center gap-1 font-semibold text-greyscale-400">
          <span>Used Delivery Credit: </span>
          <span className="text-greyscale-700">
            {subscriptionData ? Number(subscriptionData.usedCredits) : "0"}
          </span>
        </p>

        <p className="flex items-center gap-1 font-semibold text-greyscale-400">
          <span>Available Delivery Credit: </span>
          <span className="text-greyscale-700">
            {subscriptionData
              ? String(subscriptionData?.remainingCredits)
              : "0"}
          </span>
        </p>
      </div>

      {/* Subscription History Table */}
      {add ? (
        <form onSubmit={handleSubmit} className="p-4 w-max mx-auto space-y-4">
          {/* <h2 className="text-xl font-bold">Add Credit</h2> */}
          <div className="flex items-end gap-4">
            <div>
              <label htmlFor="credit" className="block font-medium">
                Credit
              </label>
              <input
                type="number"
                id="credit"
                value={credit}
                placeholder="Credit"
                onChange={(e) => setCredit(Number(e.target.value))}
                className="border p-2 rounded w-full"
              />
              {error && <div className="text-red-500">{error}</div>}
              {success && <div className="text-green-600">{success}</div>}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="bg-orange-500 text-white px-4 py-2 rounded disabled:opacity-50"
            >
              {loading ? "Adding..." : "Add Credit"}
            </button>
          </div>
        </form>
      ) : (
        <div className="bg-white rounded-lg py-6 border-t border-gray-200">
          {isLoadingHistory ? (
            <div className="text-center py-6 text-gray-400">
              Loading subscription history...
            </div>
          ) : allSubscriptions?.items?.length === 0 ? (
            <div className="text-center py-6 text-gray-400">
              No subscription history found for this user.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table className="min-w-full text-left divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <RowHeader className="text-left">
                      <IDHeader className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                        Transaction ID
                      </IDHeader>
                      <IDHeader className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Subscribed Date
                      </IDHeader>
                      <IDHeader className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Expires Date
                      </IDHeader>
                      <IDHeader className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </IDHeader>
                      <IDHeader className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </IDHeader>
                    </RowHeader>
                  </thead>

                  <tbody className="bg-white divide-y divide-gray-200">
                    {allSubscriptions?.items?.map((item: any) => (
                      <Row
                        key={item.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <ID className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.id}
                        </ID>

                        <ID className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.createdAt
                            ? format(parseISO(item.createdAt), "MMM dd, yyyy")
                            : "N/A"}
                        </ID>

                        <ID className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.user?.UserSubcription?.expiresIn
                            ? format(
                                parseISO(item.user?.UserSubcription?.expiresIn),
                                "MMM dd, yyyy"
                              )
                            : "N/A"}
                        </ID>

                        <ID className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          ₦
                          {item?.amount
                            ? parseInt(item.amount).toLocaleString()
                            : "N/A"}
                        </ID>

                        <ID className="px-6 py-4 whitespace-nowrap text-sm">
                          <span
                            className={`px-4 py-1 text-xs inline-flex text-xs leading-5 font-semibold rounded-full
                          ${
                            item.status === "SUCCESS"
                              ? "bg-green-100 text-green-800"
                              : item.status === "PENDING"
                              ? "bg-orange-100 text-orange-800"
                              : item.status === "CANCELLED"
                              ? "bg-red-100 text-red-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                          >
                            {item.status || "N/A"}
                          </span>
                        </ID>
                      </Row>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination Controls */}
              {allSubscriptions?.meta?.total > limit && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                  <div className="text-sm text-gray-500">
                    Showing page {currentPage} of{" "}
                    {Math.ceil(allSubscriptions.meta.total / limit)}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-4 py-2 text-sm font-medium rounded-md border ${
                        currentPage === 1
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Previous
                    </button>

                    {getPageNumbers().map((page, index) =>
                      page === "..." ? (
                        <span key={index} className="px-4 py-2">
                          ...
                        </span>
                      ) : (
                        <button
                          key={index}
                          onClick={() => handlePageChange(Number(page))}
                          className={`px-4 py-2 text-sm font-medium rounded-md border ${
                            currentPage === page
                              ? "bg-blue-500 text-white"
                              : "bg-white text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {page}
                        </button>
                      )
                    )}

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={
                        currentPage ===
                        Math.ceil(allSubscriptions.meta.total / limit)
                      }
                      className={`px-4 py-2 text-sm font-medium rounded-md border ${
                        currentPage ===
                        Math.ceil(allSubscriptions.meta.total / limit)
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscribeAction;

// Styled components remain the same
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Row = styled.tr``;

const RowHeader = styled.tr`
  background-color: #f9fafb;
`;

const ID = styled.td`
  font-size: 16px;
  color: #333333;
  padding: 12px;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IDHeader = styled.th`
  font-size: 16px;
  color: #333333;
  padding: 12px;
  text-align: left;
  font-weight: 700;
  white-space: nowrap;
`;

const View = styled.td`
  font-size: 16px;
  color: ${colours.orange};
  padding: 12px;
  font-weight: 500;
  white-space: nowrap;
`;
